import { createContext, useContext, useState, useEffect } from 'react';
import { auth, db } from '../services/firebase';
import { 
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';

const AuthContext = createContext();

const GLOBAL_ADVANCE_ACCESS_EMAILS = [
  'abigail@clubdelabarba.cl',
  'fernanda@clubdelabarba.cl',
];

const getNormalizedEmail = (userDoc) => String(userDoc?.email || '').trim().toLowerCase();

const canManageVacations = (userDoc) => (
  getNormalizedEmail(userDoc) === 'gerencia@clubdelabarba.cl'
);

const hasGlobalAdvanceAccess = (userDoc) => {
  if (!userDoc) return false;

  if (userDoc.role === 'admin') {
    return true;
  }

  return GLOBAL_ADVANCE_ACCESS_EMAILS.includes(getNormalizedEmail(userDoc));
};

const hasPendingAdvanceSummaryAccess = (userDoc) => {
  return hasGlobalAdvanceAccess(userDoc);
};

const hasGlobalAdvanceRequestsAccess = (userDoc) => {
  return hasGlobalAdvanceAccess(userDoc);
};

const hasGlobalAdvanceRecordsAccess = (userDoc) => {
  return hasGlobalAdvanceAccess(userDoc);
};

const hasCommissionManagerAccess = (userDoc) => {
  return userDoc?.role === 'admin' || userDoc?.sucursalId === 'nunoa';
};

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userDoc, setUserDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  async function login(email, password) {
    try {
      console.log('Intentando iniciar sesión con:', email);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      console.log('Usuario autenticado:', userCredential.user.uid);
      
      const userDocRef = doc(db, 'users', userCredential.user.uid);
      const userDocSnap = await getDoc(userDocRef);
      
      if (!userDocSnap.exists()) {
        console.error('No se encontró el documento del usuario en Firestore');
        await firebaseSignOut(auth);
        throw new Error('No se encontró la información del usuario');
      }
      
      const userData = userDocSnap.data();
      console.log('Documento del usuario encontrado:', userData);

      // Verificar campos requeridos
      if (!userData.role) {
        console.error('El documento del usuario no tiene rol asignado');
        await firebaseSignOut(auth);
        throw new Error('Su cuenta no tiene un rol asignado. Por favor, contacte al administrador.');
      }

      // Si no es admin, verificar sucursal
      if (userData.role !== 'admin' && !userData.sucursalId) {
        console.error('El documento del usuario no tiene sucursal asignada');
        await firebaseSignOut(auth);
        throw new Error('Su cuenta no tiene una sucursal asignada. Por favor, contacte al administrador.');
      }

      setUserDoc({
        ...userData,
        uid: userCredential.user.uid,
        email: userCredential.user.email
      });
      
      return userData;
    } catch (error) {
      console.error('Error en login:', error);
      let message = '';
      switch (error.code) {
        case 'auth/invalid-credential':
          message = 'Credenciales inválidas';
          break;
        case 'auth/user-not-found':
          message = 'Usuario no encontrado';
          break;
        case 'auth/wrong-password':
          message = 'Contraseña incorrecta';
          break;
        case 'auth/too-many-requests':
          message = 'Demasiados intentos fallidos. Por favor, intente más tarde';
          break;
        case 'auth/network-request-failed':
          message = 'Error de conexión. Por favor, verifique su conexión a internet';
          break;
        default:
          message = error.message;
      }
      throw new Error(message);
    }
  }

  async function logout() {
    setUserDoc(null);
    await firebaseSignOut(auth);
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('Estado de autenticación cambiado:', user?.uid);
      try {
        if (user) {
          const userDocRef = doc(db, 'users', user.uid);
          const userDocSnap = await getDoc(userDocRef);
            
          if (userDocSnap.exists()) {
            const userData = userDocSnap.data();
            console.log('Documento del usuario cargado:', userData);
              
            // Verificar campos requeridos
            if (!userData.role) {
              console.error('El documento del usuario no tiene rol asignado');
              await firebaseSignOut(auth);
              setUserDoc(null);
              setError('Su cuenta no tiene un rol asignado. Por favor, contacte al administrador.');
            } else {
              // Si es admin o tiene sucursalId, proceder normalmente
              if (userData.role === 'admin' || userData.sucursalId) {
                setUserDoc({
                  ...userData,
                  uid: user.uid,
                  email: user.email
                });
                setCurrentUser(user);
              } else {
                console.error('El documento del usuario no tiene sucursal asignada');
                await firebaseSignOut(auth);
                setUserDoc(null);
                setError('Su cuenta no tiene una sucursal asignada. Por favor, contacte al administrador.');
              }
            }
          } else {
            console.error('No se encontró el documento del usuario');
            await firebaseSignOut(auth);
            setUserDoc(null);
            setError('No se encontró la información del usuario');
          }
        } else {
          setUserDoc(null);
          setCurrentUser(null);
        }
      } catch (error) {
        console.error('Error general en onAuthStateChanged:', error);
        setError(error.message);
        setUserDoc(null);
        setCurrentUser(null);
      } finally {
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userDoc,
    isAdmin: userDoc?.role === 'admin',
    canManageCommissions: hasCommissionManagerAccess(userDoc),
    canViewPendingAdvanceSummary: hasPendingAdvanceSummaryAccess(userDoc),
    canManageAdvanceRequestsGlobal: hasGlobalAdvanceRequestsAccess(userDoc),
    canManageAdvanceRecordsGlobal: hasGlobalAdvanceRecordsAccess(userDoc),
    canManageVacations: canManageVacations(userDoc),
    login,
    logout,
    loading,
    error
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
} 
